<template lang="pug">
.option-target-other-user-list-modal
  // modal
  b-modal(v-model="_value" size="xl" @ok="ok" no-close-on-backdrop no-close-on-esc)
    template(#modal-header="{ ok }")
      .option-target-other-user-list-modal-header
        .d-flex.justify-content-start.align-items-center
          .d-flex.justify-content-start
            span.menu-title {{ _title }}
          .d-flex.justify-content-end.flex-fill(v-if="_showPdfDownloadBtn == true")
            button.btn(@click="onPdfDownload" type="button" style="width: 162px; max-width: 162px;") PDFダウンロード
          .d-flex.justify-content-end.flex-fill(v-if="_showCsvDownloadBtn == true")
            button.btn(@click="onCsvDownload" type="button" style="width: 162px; max-width: 162px;") CSVダウンロード
      button.close(type="button" @click="ok") ×

    .option-target-other-user-list-modal-body
      OptionTargetOtherUserTable(
        v-model="_items"
        :fields="fields"
        :isLoading="isLoading"
      )

    template(#modal-footer="{ ok }")
      b-button.btn.modal-ok-btn(@click="ok") 閉じる
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";

import { userStore, fontsStore } from "@/store/store";

import { SendDocumentsOptionTargetOtherUsersPdf } from "@/commons/utils/SendDocumentsOptionTargetOtherUsersPdf";

import OptionTargetOtherUserTable, { OptionTargetOtherUserTableField, OptionTargetOtherUserTableItem } from "@/pc/containers/sendDocuments/OptionTargetOtherUserTable.vue";
import { SendDocumentGetModel } from "@/commons/services/SendDocuments";
import { FacilityGetModel } from "@/commons/services/Facilities";

/**
 * ### 文書送付、確認・同意ユーザー一覧モーダル
 */
@Component({
  components: {
    OptionTargetOtherUserTable
  }
})
export default class OptionTargetOtherUserListModal extends Vue {
  /** 表示可否 */
  @Prop({ required: true })
  value!: boolean;
  /** テーブル行情報 */
  @Prop({ required: true })
  items!: Array<OptionTargetOtherUserTableItem>;
  /** 読込み中か否か */
  @Prop({ required: true })
  isLoading!: boolean;
  /** モーダル種別(同意か確認か) */
  @Prop()
  modalType!: OptionTargetOtherUserListModalType;
  /** PDFで使用する文書送付 */
  @Prop()
  sendDocument!: SendDocumentGetModel;

  /** テーブル列情報 */
  fields: Array<OptionTargetOtherUserTableField> = [];

  /** 施設名 */
  @Getter("getFacility") facility!: FacilityGetModel;

  get _value(): boolean {
    return this.value;
  }

  set _value(newValue: boolean) {
    this.$emit("input", newValue);
    return;
  }

  get _items(): Array<OptionTargetOtherUserTableItem> {
    return this.items;
  }

  set _items(newValue: Array<OptionTargetOtherUserTableItem>) {
    this.$emit("update:items", newValue);
    return;
  }

  get _title(): string {
    switch (this.modalType) {
      case OptionTargetOtherUserListModalType.AGREEMENT: {
        return "同意済一覧";
      }
      case OptionTargetOtherUserListModalType.NON_AGREEMENT: {
        return "未同意一覧";
      }
      case OptionTargetOtherUserListModalType.CONFIRM: {
        return "確認済一覧";
      }
      case OptionTargetOtherUserListModalType.NON_CONFIRM: {
        return "未確認一覧";
      }
      default: {
        return "";
      }
    }
  }

  get _showPdfDownloadBtn(): boolean {
    switch (this.modalType) {
      case OptionTargetOtherUserListModalType.AGREEMENT:
      case OptionTargetOtherUserListModalType.CONFIRM: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  get _showCsvDownloadBtn(): boolean {
    switch (this.modalType) {
      case OptionTargetOtherUserListModalType.NON_AGREEMENT: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  get _sendDocumentDatetime(): string {
    if (this.sendDocument == null) {
      return "";
    }

    const datetime: string = this.$dateUtil.convertNumberToString(this.sendDocument.sendDatetime as number, "yyyy年 mm月 dd日 hh:MM", false);

    return datetime;
  }

  /**
   * ### vue lifesycle mounted
   */
  mounted(): void {
    let fields: Array<OptionTargetOtherUserTableField> = this.createFields();
    this.fields = fields;

    return;
  }

  /**
   * ### 列名を作成する
   */
  private createFields(): Array<OptionTargetOtherUserTableField> {
    let fields: Array<OptionTargetOtherUserTableField> = [];

    fields.push({ key: "groupName", label: "グループ名（種類名）", sortable: false, sortDirection: "desc", class: "group-name" });
    fields.push({ key: "memberName", label: "メンバー名（所属）", sortable: false, sortDirection: "desc", class: "member-name" });
    fields.push({ key: "memberTel", label: "電話番号", sortable: false, sortDirection: "desc", class: "member-tel" });
    fields.push({ key: "datetime", label: "返信日時", sortable: false, sortDirection: "desc", class: "datetime" });
    fields.push({ key: "message", label: "返信時の連絡事項", sortable: false, sortDirection: "desc", class: "message" });

    return fields;
  }

  ok(): void {
    this.$emit("ok");
    return;
  }

  /**
   * ### PDFダウンロードボタン押下イベント
   */
  async onPdfDownload(): Promise<void> {
    this.createAndDownloadPdf();
    return;
  }

  /**
   * ### PDFを作成する
   */
  private async createAndDownloadPdf(): Promise<void> {
    const fileName: string = this.createFileName();
    const font: string = await fontsStore.getPdfFont({ userStore });
    const sendDocumentsReportPdfUtil: SendDocumentsOptionTargetOtherUsersPdf = new SendDocumentsOptionTargetOtherUsersPdf(font);
    await sendDocumentsReportPdfUtil.createAndDownloadPdf(this._items, this.facility.name, this.sendDocument.title, this._sendDocumentDatetime, this.modalType, fileName);

    return;
  }

  /**
   * ### PDFのファイル名を作成する
   */
  private createFileName(): string {
    let fileName: string = "";
    switch (this.modalType) {
      case OptionTargetOtherUserListModalType.AGREEMENT: {
        fileName = `HitomeQコネクト-文書送付-${this.sendDocument.title}-同意済一覧.pdf`;
        break;
      }
      case OptionTargetOtherUserListModalType.CONFIRM: {
        fileName = `HitomeQコネクト-文書送付-${this.sendDocument.title}-確認済一覧.pdf`;
        break;
      }
      default: {
        fileName = "";
        break;
      }
    }

    return fileName;
  }

  /**
   * ### CSVダウンロードボタン押下イベント
   */
  onCsvDownload(): void {
    this.createAndDownloadCsv();
    return;
  }

  /**
   * ### CSVを作成してダウンロードさせる
   */
  private createAndDownloadCsv(): void {
    const csv: string = this.createCsv(this._items);
    const fileName: string = this.createCsvFileName();
    this.downloadCsv(csv, fileName);
    return;
  }

  /**
   * ### 未同意施設関係者グループメンバーのCSVを作成する
   *
   * - 1行目の先頭はexcel等での文字化け対策でBOMを付与する
   * - 1行目は項目名を作る
   * - 2行目以降は行情報にあたる情報を作成して出力する
   */
  private createCsv(items: Array<OptionTargetOtherUserTableItem>): string {
    const csv: string[] = [];

    const header: string[] = ["グループ名", "種類名", "メンバー名", "所属", "電話番号"];
    csv.push(`\ufeff${header.join(",")}`);

    for (const item of items) {
      const body: string[] = [];
      body.push(item.name);
      body.push(item.groupTypeName);
      body.push(item.memberName);
      body.push(item.memberOrganization);
      body.push(item.memberTel);

      csv.push(body.join(","));
    }

    return csv.join("\n");
  }

  /**
   * ### CSVのファイル名を作成する
   */
  private createCsvFileName(): string {
    let fileName: string = "";
    switch (this.modalType) {
      case OptionTargetOtherUserListModalType.NON_AGREEMENT: {
        fileName = `HitomeQコネクト-文書送付-${this.sendDocument.title}-未同意一覧.csv`;
        break;
      }
      default: {
        fileName = "";
        break;
      }
    }

    return fileName;
  }

  /**
   * ### CSVファイルをダウンロードさせる
   *
   * - CSVの内容からblobファイルを作成する
   * - blobファイルの種別はtext/csv
   * - a要素を作ってそれを疑似的にクリックさせることでダウンロードさせる
   */
  private downloadCsv(csv: string, fileName: string): void {
    const blob: Blob = new Blob([csv], { type: "text/csv" });
    let link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    return;
  }
}

export enum OptionTargetOtherUserListModalType {
  UNKNOWN = -1,
  AGREEMENT = 0,
  NON_AGREEMENT = 1,
  CONFIRM = 2,
  NON_CONFIRM = 3
}
</script>
<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.option-target-other-user-list-modal {
  .pdf-contetns {
    // A4用紙相当のサイズを取る
    width: 159mm;
    height: 209mm;
    padding: 10mm;

    .datetime {
      font-size: 10px;
    }
    .title {
      font-size: 18px;
      font-weight: bold;
    }
    .send-document-infos {
      font-size: 10px;
    }

    .contents {
      font-size: 10px;
      margin-left: auto;
      margin-right: auto;

      table {
        width: 100%;
        border: 1px solid $border-darkgray;

        thead {
          background-color: $bg-header;
          text-align: center;
        }
        th,
        td {
          padding: 0 0 4px 4px;
          border: 1px solid $border-darkgray;
        }
        .other-user-group,
        .member-name,
        .family-name {
          width: 220px;
        }
        .reply-datetime {
          width: 200px;
        }
      }
      margin-bottom: 40px;
    }
  }
}

.pageout {
  position: fixed;
  z-index: -100;
}
</style>
